import Popper from '@uikit/ui-kit/src/utils/vue-popper';
import { debounce } from 'throttle-debounce';
import { getFirstComponentChild } from '@uikit/ui-kit/src/utils/vdom';
import Vue from 'vue';

export default {
  name: 'ElTooltip',

  mixins: [Popper],

  props: {
    trigger: {
      type: String,
      default: 'hover'
    },
    openDelay: {
      type: Number,
      default: 500
    },
    disabled: Boolean,
    manual: Boolean,
    popperClass: String,
    content: [String, Number],
    visibleArrow: {
      default: true
    },
    transition: {
      type: String,
      default: 'el-fade-in-linear'
    },
    popperOptions: {
      default() {
        return {
          boundariesPadding: 10,
          gpuAcceleration: false
        };
      }
    },
    enterable: {
      type: Boolean,
      default: true
    },
    hideAfter: {
      type: Number,
      default: 0
    },
    placement: {
      type: String,
      default: 'top'
    },
    maxWidth: {
      type: [Number, String],
      default: 'default',
      validator(value) {
        const type = typeof value;
        return type === 'number' && value > 0
          || type === 'string' && ['default', 'auto-width'].includes(value);
      }
    }
  },

  data() {
    return {
      timeoutPending: null
    };
  },

  beforeCreate() {
    if (this.$isServer) return;

    this.popperVM = new Vue({
      data: { node: '' },
      render() {
        return this.node;
      }
    }).$mount();

    this.debounceClose = debounce(200, () => this.handleClosePopper());
  },

  render() {
    /* istanbul ignore else */
    if (this.popperVM) {
      const styles = typeof this.maxWidth === 'number'
        ? { maxWidth: `${this.maxWidth}px` } // override css rule for default value
        : {};

      this.popperVM.node = (
        <transition
          name={ this.transition }
          onAfterLeave={ this.doDestroy }>
          <div
            onMouseleave={ this.hide }
            onMouseenter={ this.show }
            ref="popper"
            role="tooltip"
            aria-hidden={ (this.disabled || !this.showPopper) ? 'true' : 'false' }
            v-show={!this.disabled && this.showPopper}
            class={
              [
                'el-tooltip__popper',
                'qa-tooltip-popper',
                this.maxWidth === 'auto-width' ? 'el-tooltip__popper--auto-width' : '',
                'el-popper',
                this.popperClass
              ]
            }
            style={styles}
          >
            { this.$slots.content || this.content }
          </div>
        </transition>);
    }

    if (!this.$slots.default || !this.$slots.default.length) return this.$slots.default;

    const vnode = getFirstComponentChild(this.$slots.default);
    if (!vnode) return vnode;
    const data = vnode.data = vnode.data || {}; // eslint-disable-line no-multi-assign
    const on = vnode.data.on = vnode.data.on || {}; // eslint-disable-line no-multi-assign
    const nativeOn = vnode.data.nativeOn = vnode.data.nativeOn || {}; // eslint-disable-line no-multi-assign

    data.staticClass = this.concatClass(data.staticClass, 'el-tooltip');

    if (this.trigger === 'click') {
      on.click = this.addEventHandle(on.mouseenter, this.toggle);
      nativeOn.click = this.addEventHandle(on.mouseenter, this.toggle);
    } else {
      on.mouseenter = this.addEventHandle(on.mouseenter, this.show);
      on.mouseleave = this.addEventHandle(on.mouseleave, this.hide);
      nativeOn.mouseenter = this.addEventHandle(nativeOn.mouseenter, this.show);
      nativeOn.mouseleave = this.addEventHandle(nativeOn.mouseleave, this.hide);
    }

    return vnode;
  },

  mounted() {
    this.referenceElm = this.$el;
  },

  updated() {
    if (this.referenceElm !== this.$el) {
      this.referenceElm = this.$el;
    }
  },

  methods: {
    show() {
      this.setExpectedState(true);
      this.handleShowPopper();
    },

    hide() {
      this.setExpectedState(false);
      this.debounceClose();
    },

    toggle() {
      this.expectedState ? this.hide() : this.show();
    },

    addEventHandle(old, fn) {
      if (!old) {
        return fn;
      }
      if (Array.isArray(old)) {
        return old.indexOf(fn) > -1 ? old : old.concat(fn);
      }
      return old === fn ? old : [old, fn];
    },

    concatClass(a, b) {
      if (a && a.indexOf(b) > -1) return a;
      return a ? b ? (`${a} ${b}`) : a : (b || '');
    },

    handleShowPopper() {
      if (!this.expectedState || this.manual) return;
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.showPopper = true;
      }, this.openDelay);

      if (this.hideAfter > 0) {
        this.timeoutPending = setTimeout(() => {
          this.showPopper = false;
        }, this.hideAfter);
      }
    },

    handleClosePopper() {
      if (this.enterable && this.expectedState || this.manual) return;
      clearTimeout(this.timeout);

      if (this.timeoutPending) {
        clearTimeout(this.timeoutPending);
      }
      this.showPopper = false;
    },

    setExpectedState(expectedState) {
      if (expectedState === false) {
        clearTimeout(this.timeoutPending);
      }
      this.expectedState = expectedState;
    }
  }
};
